"use client"

import { useTranslation } from "react-i18next"
import { Ticket } from "lucide-react"
import { useAppointmentModal } from "@/contexts/AppointmentModalContext"
import { useRecordAbonements } from "@/hooks/modal/useRecordAbonements"
import { currentUses, optionsForLine, setUse } from "@/lib/appointment/abonements"

/** The lines a client package pays, for the payment section (abonements.ts). */
export function usePackageUses() {
  const { appointment } = useAppointmentModal()
  const serviceIds = (Array.isArray(appointment?.services) ? appointment!.services : []).map((s: any) => Number(s.id))
  const { data } = useRecordAbonements(appointment?.id, serviceIds)
  return { data, uses: currentUses((appointment?.paymentSection as any)?.abonementUses, data) }
}

/**
 * "Pay with package" under a service line: shown only when one of the client's
 * packages covers the line (or already pays it). Picking one makes the line owe
 * no money; the visit is taken from the package when the appointment is saved.
 */
export function PackagePaySelect({ serviceId, className }: { serviceId: number; className?: string }) {
  const { t } = useTranslation()
  const { updateAppointment } = useAppointmentModal()
  const { data, uses } = usePackageUses()
  const options = optionsForLine(data, Number(serviceId), uses)
  if (!options.length) return null
  const current = uses.find((u) => Number(u.service_id) === Number(serviceId))

  return (
    <label className={className ?? "col-span-12 flex items-center gap-2 text-xs text-gray-600 -mt-1"}>
      <Ticket className="h-3.5 w-3.5 text-rose-500" aria-hidden="true" />
      <span>{t("appointment.payment.package", "Pay with package")}</span>
      <select
        className="rounded border border-gray-300 bg-white px-2 py-1 text-xs"
        value={current?.abonement_id ?? ""}
        onChange={(e) =>
          updateAppointment({
            paymentSection: {
              abonementUses: setUse(uses, Number(serviceId), e.target.value ? Number(e.target.value) : null),
            },
          } as any)
        }
      >
        <option value="">{t("appointment.payment.package_none", "No, pay with money")}</option>
        {options.map((o) => (
          <option key={o.abonement_id} value={o.abonement_id}>
            {[o.number, o.title].filter(Boolean).join(" · ")}
            {o.left != null ? ` · ${t("appointment.payment.package_left", "{{n}} left", { n: o.left })}` : ""}
          </option>
        ))}
      </select>
    </label>
  )
}
